export const QUALITY_REVIEW_MAX_FILE_BYTES = 25 * 1024 * 1024

const SOURCE_FIELDS = [
    'expectedCompanyName',
    'expectedUen',
    'expectedRegisteredAddress',
    'expectedDirectors',
    'expectedCompanySecretaries',
    'expectedCountryOfDomicile'
]

export function buildSourceConfirmationKey(expectations, bizfileKey) {
    const sourceValues = SOURCE_FIELDS.map(field =>
        String(expectations?.[field] || '').replace(/\s+/g, ' ').trim()
    )
    return [String(bizfileKey || ''), ...sourceValues].join('|')
}

export function buildReviewReadiness({
    hasBizfile,
    extractionReady,
    sourceConfirmed,
    documentCount,
    financialPeriodReady,
    financialPeriodInvalid
}) {
    return [
        {
            id: 'bizfile',
            ready: Boolean(hasBizfile),
            label: hasBizfile ? 'BizFile uploaded' : 'Upload the BizFile'
        },
        {
            id: 'extraction',
            ready: Boolean(extractionReady),
            label: extractionReady ? 'BizFile information extracted' : 'Extract the BizFile information'
        },
        {
            id: 'confirmation',
            ready: Boolean(sourceConfirmed),
            label: sourceConfirmed ? 'Source information confirmed' : 'Confirm the source information'
        },
        {
            id: 'period',
            ready: Boolean(financialPeriodReady) && !financialPeriodInvalid,
            label: financialPeriodInvalid
                ? 'Correct the financial period dates'
                : financialPeriodReady
                    ? 'Financial period ready'
                    : 'Enter the financial period'
        },
        {
            id: 'documents',
            ready: Number(documentCount || 0) > 0,
            label: Number(documentCount || 0) > 0
                ? `${documentCount} review document${documentCount === 1 ? '' : 's'} uploaded`
                : 'Upload at least one review document'
        }
    ]
}

export function buildSetupStepVisibility({ extractionReady, sourceConfirmed }) {
    return {
        showStepOne: true,
        showStepTwo: Boolean(extractionReady),
        showStepThree: Boolean(extractionReady && sourceConfirmed),
        activeStep: extractionReady ? (sourceConfirmed ? 3 : 2) : 1
    }
}

export function validateReviewFiles(files, {
    accept = '',
    existingFiles = [],
    reservedFiles = [],
    maxFiles = 30,
    multiple = true,
    maxBytes = QUALITY_REVIEW_MAX_FILE_BYTES
} = {}) {
    const acceptedExtensions = String(accept || '')
        .split(',')
        .map(item => item.trim().toLowerCase())
        .filter(Boolean)
    const accepted = []
    const errors = []
    const seen = new Set((existingFiles || []).map(fileIdentity))
    const seenNames = new Set([...(existingFiles || []), ...(reservedFiles || [])].map(fileNameIdentity))

    for (const file of Array.isArray(files) ? files : []) {
        const extension = `.${String(file?.name || '').split('.').pop()?.toLowerCase() || ''}`
        if (acceptedExtensions.length && !acceptedExtensions.includes(extension)) {
            errors.push(`${file?.name || 'File'}: unsupported file type.`)
            continue
        }
        if (Number(file?.size || 0) > maxBytes) {
            errors.push(`${file?.name || 'File'}: larger than the 25 MB limit.`)
            continue
        }

        const key = fileIdentity(file)
        const nameKey = fileNameIdentity(file)
        if (seen.has(key) || seenNames.has(nameKey)) {
            errors.push(`${file?.name || 'File'}: already added.`)
            continue
        }
        if ((existingFiles.length + accepted.length) >= maxFiles) {
            errors.push(`A maximum of ${maxFiles} files can be added to this section.`)
            break
        }

        seen.add(key)
        seenNames.add(nameKey)
        accepted.push(file)
        if (!multiple) break
    }

    return {
        accepted: multiple ? accepted : accepted.slice(0, 1),
        errors
    }
}

export async function assertReviewFilesReadable(files) {
    for (const file of Array.isArray(files) ? files : []) {
        if (!file) continue
        try {
            const probe = typeof file.slice === 'function' ? file.slice(0, 1) : file
            if (typeof probe?.arrayBuffer === 'function') {
                await probe.arrayBuffer()
            } else if (typeof file.arrayBuffer === 'function') {
                await file.arrayBuffer()
            }
        } catch (error) {
            throw new Error(`"${file.name || 'Selected file'}" can no longer be read by the browser. Please remove it, select it again, and retry.`)
        }
    }
    return true
}

export function fileIdentity(file) {
    return `${file?.name || 'file'}-${file?.size || 0}-${file?.lastModified || 0}`
}

export function fileNameIdentity(file) {
    return String(file?.name || 'file').normalize('NFKC').trim().toLowerCase()
}
